import { Box, Button, Flex, Heading, Stack, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChangeEvent, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ImportPending } from '../components/ImportPending';
import { RatesNav } from '../components/Rates/RatesNav';
import { RatesPage } from '../components/Rates/RatesPage';
import { SimpleCheckbox } from '../components/SimpleCheckbox';
import { SimpleForm } from '../components/SimpleForm';
import { SimpleInput } from '../components/SimpleInput';
import { SimpleSelect } from '../components/SimpleSelect';
import { trpc } from '../config/trpc';

type SingleUpdate = {
  count: number;
  total: number;
};

export type Updates = {
  creates: SingleUpdate;
  segments: SingleUpdate;
  upserts: SingleUpdate;
};

const schema = z.object({
  sheet: z.string({ required_error: 'Required' }),
  workbook: z.string({ required_error: 'Required' }),
  confirmRates: z.boolean().refine((val) => val, {
    message: 'You must confirm all rates are correct',
  }),
});

export const ImportScreen = () => {
  const toast = useToast();

  const [sheetId, setSheetId] = useState(null);
  const [selectedWorkbook, setSelectedWorkbook] = useState(null);

  const [isImporting, setIsImporting] = useState(false);
  const [isFetchingSheet, setIsFetchingSheet] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [, setTotal] = useState(0);
  const [updates, setUpdates] = useState<Updates>({
    creates: {
      count: null,
      total: null,
    },
    segments: {
      count: null,
      total: null,
    },
    upserts: {
      count: null,
      total: null,
    },
  });

  const [logs, setLogs] = useState<string[]>([]);
  const [isFinishedImporting, setIsFinishedImporting] = useState(false);
  const { data: workbooks } = trpc.getSheetPages.useQuery({ id: sheetId }, { enabled: !!sheetId });
  const { data: tableInfo, isLoading: isTableLoading } = trpc.getTableInfo.useQuery(
    {
      sheet: selectedWorkbook,
      id: sheetId,
      includeRows: false,
    },
    { enabled: !!selectedWorkbook && !!sheetId },
  );

  const methods = useForm<Partial<any>>({ mode: 'onBlur', resolver: zodResolver(schema) });

  useEffect(() => {
    if (workbooks?.length > 0) {
      setSelectedWorkbook(workbooks?.[0]);
      setIsFetchingSheet(false);
      methods.setValue('workbook', workbooks?.[0]);
      setIsValidating(true);
    }
  }, [methods, workbooks]);

  trpc.importRates.useSubscription(
    {
      id: sheetId,
      sheet: selectedWorkbook,
    },
    {
      onStarted() {
        setIsImporting(true);
      },
      onData: (data: any) => {
        const newLogs = structuredClone(logs);
        const newUpdates = structuredClone(updates);

        if (data.type === 'log') {
          newLogs.push(data.message);
        } else if (data.type === 'info') {
          newUpdates.creates.total = data.creates;
          newUpdates.segments.total = data.segments;
          newUpdates.upserts.total = data.upserts;
          newUpdates.creates.count = newUpdates.creates.count || 0;
          newUpdates.segments.count = newUpdates.segments.count || 0;
          newUpdates.upserts.count = newUpdates.upserts.count || 0;
        } else if (data.type === 'create') {
          newUpdates.creates.count += data.count;
        } else if (data.type === 'upsert') {
          newUpdates.upserts.count += data.count;
        } else if (data.type === 'segment') {
          newUpdates.segments.count += data.count;
        } else if (data.type === 'done') {
          setIsFinishedImporting(true);
          logs.push('Done!');
          setIsImporting(false);
        }
        flushSync(() => {
          setLogs(newLogs);
          setUpdates(newUpdates);
        });
      },
      onError: (error) => {
        setIsImporting(false);
        toast({
          title: 'Something went wrong',
          description: error?.message,
          status: 'error',
        });
      },
      enabled: isImporting,
    },
  );

  const parseSheetUrl = (event: any) => {
    const incomingSheetUrl = event?.target?.value?.match(/[0-9a-zA-Z\-_]{44}/g)?.at([0]);
    setSheetId(incomingSheetUrl);

    if (!incomingSheetUrl) return;
    setIsFetchingSheet(true);
  };

  const handleWorkbookChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const incomingWorkbook = event.target.value;
    if (!incomingWorkbook) return;
    setSelectedWorkbook(event.target.value);
  };

  const handleReset = () => {
    setTotal(0);
    setIsImporting(false);
    setUpdates({
      creates: {
        count: 0,
        total: 0,
      },
      segments: {
        count: 0,
        total: 0,
      },
      upserts: {
        count: 0,
        total: 0,
      },
    });
    setLogs([]);
    setIsFinishedImporting(false);
    setSheetId(null);
    methods.reset({ sheet: null, workbook: null, confirmRates: false });
  };

  const onValid = () => {
    try {
      methods.trigger();
      if (!methods.formState.isValid) {
        toast({
          title: 'Invalid form',
          description: `Please fill out all required fields: ${JSON.stringify(methods.formState.errors, null, 2)}`,
          status: 'error',
        });
        return;
      }

      setIsImporting(true);
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: error?.message,
        status: 'error',
      });
    }
  };
  const handleSubmit = methods.handleSubmit(onValid);

  useEffect(() => {
    if (tableInfo?.provider || tableInfo?.errors?.length) {
      setIsValidating(false);
    }
  }, [tableInfo?.provider, tableInfo?.errors]);

  return (
    <RatesPage>
      <Flex direction="column" borderRightWidth={1} minW="lg">
        <RatesNav />
        <Stack spacing="4" p="6" h="full" overflowY="auto">
          <Stack direction="row" align="center" justify="space-between">
            <Heading size="md">New Import</Heading>
          </Stack>
          <SimpleForm defaultValues={{}} {...methods}>
            <Stack as="form" overflowY="auto" onSubmit={handleSubmit}>
              <Stack justify="flex-start" flex="1">
                <Stack direction="row" align="flex-end">
                  <SimpleInput name="sheet" label="Google Sheet URL" onBlur={parseSheetUrl} />
                </Stack>
                <SimpleSelect
                  name="workbook"
                  label="Select Workbook"
                  isDisabled={!workbooks?.length}
                  onChange={handleWorkbookChange}
                  options={workbooks?.map((workbook) => ({
                    value: workbook,
                    label: workbook,
                  }))}
                />
              </Stack>

              <Stack justify="flex-end">
                <SimpleCheckbox name="confirmRates" colorScheme="brand" size="md" label="All rates are correct" />
                <Button
                  colorScheme="brand"
                  isLoading={isImporting}
                  isDisabled={!!tableInfo?.errors?.length || isTableLoading || !tableInfo}
                  type="submit"
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
          </SimpleForm>
        </Stack>
      </Flex>
      <Box w="full" h="full" overflowY="auto">
        <ImportPending
          isImporting={isImporting}
          isFetchingSheet={isFetchingSheet}
          isValidating={isValidating}
          tableInfo={tableInfo}
          updates={updates}
          logs={logs}
          handleReset={handleReset}
          isFinishedImporting={isFinishedImporting}
        />
      </Box>
    </RatesPage>
  );
};
